'use client'

import { clsx } from 'clsx'
import { useTranslation } from 'next-i18next'
import { type FormEventHandler, type ReactNode } from 'react'
import { IconButton } from 'ui/src/components/Button/IconButton'
import { ToggleTip } from 'ui/src/components/Tooltip/ToggleTip'
import { InfoIconOutlined } from 'ui/src/icons/InfoIconOutlined'
import { xStack, yStack } from 'ui/src/patterns/stack'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import { tokens } from 'ui/src/theme/tokens.css'
import { useHandleSubmitPriceCalculatorSection } from '@/components/PriceCalculator/useHandleSubmitPriceCalculatorSection'
import { useTranslateFieldLabel } from '@/components/PriceCalculator/useTranslateFieldLabel'
import { TextLink } from '@/components/TextLink/TextLink'
import { TextWithLink } from '@/components/TextWithLink'
import { useShowFetchInsurance } from '@/features/insurely/useFetchInsurance'
import { EditSsnWarningContainer } from '@/features/priceCalculator/PurchaseFormV2/InsuranceDataForm/EditSsnWarningContainer'
import { FormGridNew } from '@/features/priceCalculator/PurchaseFormV2/InsuranceDataForm/FormGridNew/FormGridNew'
import {
  ssnSectionWrapper,
  gdprLink,
} from '@/features/priceCalculator/PurchaseFormV2/InsuranceDataForm/InsuranceDataForm.css'
import { useConfirmPriceIntent } from '@/features/priceCalculator/PurchaseFormV2/InsuranceDataForm/useConfirmPriceIntent'
import { useRegistrationAddressField } from '@/services/PriceCalculator/formFragments'
import {
  deserializeField,
  prefillData,
  setupForm,
  updateFormState,
} from '@/services/PriceCalculator/PriceCalculator.helpers'
import { type Form, type FormSection, type JSONData } from '@/services/PriceCalculator/PriceCalculator.types'
import type { PriceIntent } from '@/services/priceIntent/priceIntent.types'
import { useRoutingLocale } from '@/utils/l10n/useRoutingLocale'
import { PageLink } from '@/utils/PageLink'
import { useBreakpoint } from '@/utils/useBreakpoint/useBreakpoint'
import { useActiveFormSectionId, GOTO_NEXT_SECTION } from '../../useActiveFormSectionId'
import { usePriceCalculatorForm } from '../../usePriceCalculatorForm'
import { usePriceCalculatorStep } from '../../usePriceCalculatorStep'
import { usePriceIntentValueOrThrow } from '../../usePriceIntent'
import { usePriceTemplate } from '../priceTemplateAtom'
import { SectionTitle, SectionSubtitle } from '../SectionHeading'
import { FetchInsuranceContainer } from './FetchInsurance/FetchInsuranceContainer'
import { SectionNavigation } from './SectionNavigation/SectionNavigation'
import { SSN_SE_SECTION_ID, SsnSeSectionV2 } from './SsnSeSectionV2'

export function InsuranceDataForm({ className }: { className?: string }) {
  const locale = useRoutingLocale()
  const { t } = useTranslation('purchase-form')

  const form = usePriceCalculatorForm()
  const { activeFormSectionId } = useActiveFormSectionId()
  const { step } = usePriceCalculatorStep()

  const sections = form.sections.map((section, index) => {
    if (step !== 'fillForm' || section.id !== activeFormSectionId) {
      // Skip rendering inactive sections
      return null
    }

    let sectionBody: ReactNode
    const isSsnSection = section.id === SSN_SE_SECTION_ID

    if (isSsnSection) {
      sectionBody = <SsnSeSectionV2 />
    } else {
      const isLast = index === form.sections.length - 1

      sectionBody = (
        <>
          <InsuranceDataSection section={section} />
          {isLast && (
            <TextLink
              href={PageLink.privacyPolicy({ locale })}
              className={clsx(sprinkles({ alignSelf: 'center' }), gdprLink)}
              target="_blank"
              size="xs"
            >
              {t('GDPR_LINK_BEFORE_OFFER')}
            </TextLink>
          )}
        </>
      )
    }
    return (
      <div
        key={section.id}
        className={clsx(yStack({ gap: { _: 'lg', lg: 9 } }), isSsnSection && ssnSectionWrapper, className)}
      >
        {!isSsnSection && <SectionNavigation />}

        <div className={yStack({ gap: 'lg' })}>
          <SectionHeadings section={section} />
          {sectionBody}
        </div>
      </div>
    )
  })

  return (
    <>
      <EditSsnWarningContainer />
      <FetchInsuranceContainer />
      {sections}
    </>
  )
}

function SectionHeadings({ section }: { section: FormSection }) {
  const translateLabel = useTranslateFieldLabel()
  const isLarge = useBreakpoint('lg')

  return (
    <div>
      <div className={xStack({ alignItems: 'center', gap: 'xs' })}>
        <SectionTitle>{translateLabel(section.title)}</SectionTitle>
        {section.tooltip && (
          <ToggleTip
            // We show sticky header on mobile; tooltip should not be overridden by it
            side={isLarge ? 'top' : 'bottom'}
            align="start"
            alignOffset={-12}
            sideOffset={0}
            arrow={true}
            portal={false}
            collisionPadding={4}
            Trigger={
              <IconButton
                data-no-hover
                Icon={<InfoIconOutlined size="1.25rem" color={tokens.legacyColors.fillOpaqueSecondary} />}
              />
            }
          >
            <TextWithLink
              as="span"
              size="xs"
              color="textNegative"
              href={PageLink.milage()}
              target="_blank"
              rel="noopener noreferrer"
            >
              {translateLabel(section.tooltip)}
            </TextWithLink>
          </ToggleTip>
        )}
      </div>
      {section.subtitle != null && <SectionSubtitle>{translateLabel(section.subtitle)}</SectionSubtitle>}
    </div>
  )
}

type InsuranceDataSectionProps = {
  section: FormSection
}

// TODO
// - do we need autofocus on first field?
function InsuranceDataSection({ section }: InsuranceDataSectionProps) {
  const priceTemplate = usePriceTemplate()
  const { setStep } = usePriceCalculatorStep()
  const priceIntent = usePriceIntentValueOrThrow()
  const confirmPriceIntent = useConfirmPriceIntent()
  const { activeFormSectionId, setActiveFormSectionId } = useActiveFormSectionId()

  const showFetchInsurance = useShowFetchInsurance({ priceIntentId: priceIntent.id })

  const submitPriceCalculatorSection = useHandleSubmitPriceCalculatorSection({
    onSuccess({ priceIntent, customer }) {
      const form = setupForm({
        customer,
        priceIntent,
        template: priceTemplate,
      })

      const activeSectionIndex = form.sections.findIndex((section) => section.id === activeFormSectionId)
      const isLastSection = activeSectionIndex === form.sections.length - 1
      const isReadyToConfirm = isFormReadyToConfirm({ form, priceIntent, customer })

      // Only calculate price if we are on the last section and all sections are valid
      if (isLastSection && isReadyToConfirm) {
        setStep('calculatingPrice')
        confirmPriceIntent()
        return
      }

      // 'CurrentInsuranceField' prompts insurely as well which ideally would cover all the cases.
      // For car however we want to prompt it while submitting the form as we don't have 'CurrentInsuranceField' for i.
      if (priceIntent.externalInsurer) {
        showFetchInsurance()
      }

      setActiveFormSectionId(GOTO_NEXT_SECTION)

      // Scroll to top when going to next step to avoid sticky header overlap
      window.scrollTo({ top: 0, behavior: 'instant' })
    },
  })

  const handleSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault()

    const jsonData = formDataToJson(new FormData(event.currentTarget), section.items)
    const useRegistrationAddress = jsonData[useRegistrationAddressField.name] === 'true'

    if (!useRegistrationAddress) {
      submitPriceCalculatorSection(jsonData)
      return
    }

    // If the user has modified the registration address, we should consider a new address
    const hasModifiedAddress = hasRegistrationAddressModifications(jsonData, priceIntent.suggestedData)
    submitPriceCalculatorSection({
      ...jsonData,
      [useRegistrationAddressField.name]: (!hasModifiedAddress).toString(),
    })
  }

  return (
    <form onSubmit={handleSubmit}>
      <FormGridNew items={section.items} submitLabel={section.submitLabel} />
    </form>
  )
}

type IsFormReadyToConfirmParams = {
  form: Form
  customer?: CustomerData | null
  priceIntent: PriceIntent
}
type CustomerData = {
  ssn?: string | null
  email?: string | null
}

export const isFormReadyToConfirm = ({ form, customer, priceIntent }: IsFormReadyToConfirmParams) => {
  const values = { ...priceIntent.data, ssn: customer?.ssn }

  const filledForm = prefillData({
    form,
    data: values,
  })

  const updatedForm = updateFormState({ form: filledForm, values })

  return updatedForm.sections.every((section) => section.state === 'valid')
}

const formDataToJson = (formData: FormData, sectionItems: FormSection['items']) => {
  const result: JSONData = {}

  for (const item of sectionItems) {
    const deserializedFieldValue = deserializeField(item.field, formData)
    if (deserializedFieldValue !== undefined) {
      result[item.field.name] = deserializedFieldValue
    }
  }
  return result
}

function hasRegistrationAddressModifications(formJsonData: JSONData, suggestedData: Record<string, any>) {
  const EXPOSURE_ADDRESS_FIELDS = ['street', 'zipCode']

  return EXPOSURE_ADDRESS_FIELDS.some((field) => {
    const suggestedValue = suggestedData[field]
    return formJsonData[field] != null && formJsonData[field] !== suggestedValue
  })
}
